// Signals zone — notifications, notificationPrefs, documentAI, files
import type { Notification, DocumentAIJob, DocumentTemplate, FileRecord } from './types'
import type { RequestHelper, FileUploadHelper } from './client'

export function signalsMethods(r: RequestHelper, f: FileUploadHelper) {
  return {
    // Notifications
    notifications: {
      list: (unreadOnly = false) => r.get<{ notifications: Notification[] }>(`/hub/notifications${unreadOnly ? '?unread_only=true' : ''}`),
      count: () => r.get<{ count: number }>('/hub/notifications/count'),
      markRead: (id: string) => r.put<void>(`/hub/notifications/${id}/read`),
      markAllRead: () => r.put<void>('/hub/notifications/read-all'),
    },

    // Notification Preferences
    notificationPrefs: {
      get: () => r.get<Record<string, unknown>>('/hub/notification-preferences'),
      update: (data: Record<string, unknown>) => r.put<void>('/hub/notification-preferences', data),
      mute: (projectId: string) => r.post<void>(`/hub/notification-preferences/mute/${projectId}`),
      unmute: (projectId: string) => r.del<void>(`/hub/notification-preferences/mute/${projectId}`),
    },

    // Document AI
    documentAI: {
      analyze: (data: { file_id?: string; text?: string; project_id?: string; analysis_type?: string }) => r.post<{ job: DocumentAIJob }>('/hub/document-ai/analyze', data),
      generate: (data: { template_id: string; input_data: Record<string, unknown>; project_id?: string; output_format?: string }) => r.post<{ job: DocumentAIJob }>('/hub/document-ai/generate', data),
      extract: (data: { file_id?: string; text?: string; project_id?: string; extract_type?: string }) => r.post<{ job: DocumentAIJob }>('/hub/document-ai/extract', data),
      summarize: (data: { file_id?: string; text?: string; project_id?: string; max_length?: number }) => r.post<{ job: DocumentAIJob }>('/hub/document-ai/summarize', data),
      jobs: (params?: Record<string, string>) => {
        const query = params ? `?${new URLSearchParams(params).toString()}` : ''
        return r.get<{ jobs: DocumentAIJob[] }>(`/hub/document-ai/jobs${query}`)
      },
      job: (jobId: string) => r.get<{ job: DocumentAIJob }>(`/hub/document-ai/jobs/${jobId}`),
      templates: (category?: string) => r.get<{ templates: DocumentTemplate[] }>(`/hub/document-ai/templates${category ? `?category=${category}` : ''}`),
      template: (id: string) => r.get<{ template: DocumentTemplate }>(`/hub/document-ai/templates/${id}`),
      createTemplate: (data: Partial<DocumentTemplate>) => r.post<{ template: DocumentTemplate }>('/hub/document-ai/templates', data),
    },

    // Files
    files: {
      list: (params?: Record<string, string>) => {
        const query = params ? `?${new URLSearchParams(params).toString()}` : ''
        return r.get<{ files: FileRecord[] }>(`/hub/files${query}`)
      },
      upload: (file: File, projectId?: string, context = 'general', contextId?: string): Promise<{ file: FileRecord }> => {
        return f.upload(file, projectId, context, contextId)
      },
      delete: (id: string) => r.del<void>(`/hub/files/${id}`),
    },
  }
}
